/**
 * Functions の画面の「API キーやパスワード」の欄の純粋な判断（画面は components/FunctionSecretsCard.tsx）。
 * 2026-10-04 console/secret-input（founder y: 第三者が秘密の値を盗める道をふさぐ）。
 *
 * WHY 画面で入れられるようにしたか（それまでは名前の一覧だけ・値は CLI / MCP の仕事だった）:
 *   値を入れる道が「AI のチャットに貼る（MCP の functions_secret_set）」か「端末で打つ」しか無いと、エンジニアでない人は
 *   チャットに貼る。チャットに貼った値は会話の記録・AI の提供元に残る（盗まれる道 ②）。ブラウザの password 欄で入れれば、
 *   値はこの画面 → 本体 → 実行基盤にしか通らない。MCP は既定で値を受けず、この画面へのリンク（?secret=NAME）を返す。
 *
 * 値の扱いの決まり（このファイルと画面で守る・functionSecrets.test.ts がソースを読んで確かめる）:
 *   - 値は URL に入れない（パスは名前だけ・リンクの ?secret= は名前だけを読む）
 *   - ログに出さない・再表示しない（送ったら成功でも失敗でも欄を空にする）
 *   - 本体も値を保存しない（compute_secrets は名前だけ）= 「今の値を見る」は作りようがない
 *
 * 依存なし（node --test を型の除去だけで回すため）。
 */

/** 本体の BINDING_NAME_RE と同じ（apps/server/src/modules/compute/bindings.ts。テストが写し間違いを見張る） */
const SECRET_NAME_RE = /^[A-Z][A-Z0-9_]{0,62}$/;
/** KUMO_ で始まる名前は本体が入れる値（KUMO_SECRET_KEY 等）のために取ってある */
const RESERVED_PREFIX = 'KUMO_';
/** 本体の上限（serviceSecrets.ts putSecret: Buffer.byteLength > 5 KB で 400）。UTF-8 のバイトで数える */
export const SECRET_VALUE_MAX_BYTES = 5 * 1024;

export type SecretNameIssue = 'empty' | 'format' | 'reserved';

export function secretNameIssue(name: string): SecretNameIssue | null {
  if (!name) return 'empty';
  if (!SECRET_NAME_RE.test(name)) return 'format';
  if (name.startsWith(RESERVED_PREFIX)) return 'reserved';
  return null;
}

/**
 * ふつうに打った名前を保存の形にする（「stripe secret key」「openai-api-key」→ STRIPE_SECRET_KEY / OPENAI_API_KEY）。
 * WHY: エンジニアでない人に UPPER_SNAKE_CASE を覚えてもらうより、打った物を寄せる方が早い。
 *   空白・ハイフン・ドットだけを _ にし、ほかの文字（日本語など）は残す = 決まりに合わないことをそのまま見せる（勝手に別の名前にしない）
 */
export function cleanSecretName(input: string): string {
  return input
    .trim()
    .toUpperCase()
    .replace(/[\s.\-]+/g, '_')
    .replace(/_+/g, '_');
}

export type SecretValueIssue = 'empty' | 'tooLong';

export function secretValueIssue(value: string): SecretValueIssue | null {
  if (!value) return 'empty';
  if (new TextEncoder().encode(value).length > SECRET_VALUE_MAX_BYTES) return 'tooLong';
  return null;
}

/** PUT / DELETE の送り先（`…/functions/secrets/:name`）。名前だけ = 値はパスにもクエリにも入らない */
export function secretPath(functionsBase: string, name: string): string {
  return `${functionsBase}/secrets/${encodeURIComponent(name)}`;
}

/**
 * AI（MCP の functions_secret_set・CLI の案内）が渡すリンクの `?secret=NAME` から、名前の欄に入れる値を読む。
 * 決まりに合わない物は null（名前の欄に入れない）。値らしい物を名前として渡されても、記号が入れば落ちる
 */
export function secretFromSearch(search: string): string | null {
  const raw = new URLSearchParams(search).get('secret');
  if (!raw) return null;
  const name = cleanSecretName(raw);
  return secretNameIssue(name) === null ? name : null;
}

/**
 * 入れる・上書きするときに確認を挟むか。本番 = 挟む（今の確認の決まり: 本番への変更は本人がブラウザで押す。MCP の確認のリンクと同じ線）。
 * 開発用 = 挟まない（試す往復を止めない。MCP の functions_secret_set も開発用は確認なし = apps/mcp/src/tools/functions.ts の冒頭）
 */
export function confirmBeforePut(env: 'development' | 'production'): boolean {
  return env === 'production';
}

/** 消すときは環境によらず確認する（消すとその値を使う機能がすぐ動かなくなる。Kumo は値を持たないので戻せない） */
export function confirmBeforeDelete(_env: 'development' | 'production'): boolean {
  return true;
}

/** 本体のエラーの code → この欄の文言のキー。null = 共通の errorText に任せる */
export function secretErrorKey(code: string): string | null {
  switch (code) {
    // 409: Functions もアプリ（SSR）も一度も公開していない = 送り先の Worker が無い（serviceSecrets.ts secretTargets）
    case 'functions_not_deployed':
      return 'fnSecret.error.notDeployed';
    // 409: Functions がオフで、アプリ（SSR）も無い（serviceLifecycle.ts assertActive）
    case 'functions_disabled':
      return 'fnSecret.error.disabled';
    case 'rate_limited':
      return 'fnSecret.error.rateLimited';
    // DELETE で名前が無い（別のタブ・AI がもう消した）
    case 'not_found':
      return 'fnSecret.error.notFound';
    default:
      return null;
  }
}
